/**
 * Equipment Automation — pure-logic module for AC calculation and attack row building.
 * No side effects, no server dependencies.
 */
/** Extract structured inventory items from repeatingData rows. */
export function parseInventory(rows) {
    return rows.map(r => {
        const rawBaseAc = Number(r.values['base_ac']);
        const rawCat = String(r.values['armor_category'] ?? 'Light');
        return {
            rowId: r.rowId,
            name: String(r.values['name'] ?? ''),
            srd_slug: String(r.values['srd_slug'] ?? ''),
            item_type: String(r.values['item_type'] ?? 'gear'),
            equipped: Boolean(r.values['equipped']),
            base_ac: Number.isFinite(rawBaseAc) ? rawBaseAc : 0,
            armor_category: rawCat === 'Medium' || rawCat === 'Heavy' ? rawCat : 'Light',
        };
    });
}
/**
 * Find the equipped armor item (not shield). Returns undefined if none.
 *
 * Resolution order: (1) SRD catalogue via `srd_slug`; (2) Option-B manual armor
 * synthesised from `base_ac` + `armor_category` when no SRD entry resolves. The
 * synthetic object is shaped exactly like an {@link SrdEquipmentItem} so that
 * {@link calculateAC} / {@link computeAcBreakdown} apply the same DEX-cap logic:
 *   Light  → acMaxBonus null  (full DEX)
 *   Medium → acMaxBonus 2     (DEX capped at +2)
 *   Heavy  → acMaxBonus 0 / acDexBonus false (no DEX)
 */
export function findEquippedArmor(inventory, equipmentMap) {
    for (const item of inventory) {
        if (!item.equipped)
            continue;
        if (item.item_type !== 'armor')
            continue;
        const srd = equipmentMap.get(item.srd_slug);
        if (srd && srd.armorCategory && srd.armorCategory !== 'Shield')
            return srd;
        // Option B: no resolvable SRD armor → synthesise from manual base AC.
        if (!srd && typeof item.base_ac === 'number' && item.base_ac > 0) {
            const category = item.armor_category ?? 'Light';
            return {
                slug: item.srd_slug || `manual-armor-${item.rowId}`,
                name: item.name,
                category: 'Armor',
                cost: null,
                weight: null,
                armorCategory: category,
                acBase: item.base_ac,
                acDexBonus: category !== 'Heavy',
                acMaxBonus: category === 'Medium' ? 2 : category === 'Heavy' ? 0 : null,
            };
        }
    }
    return undefined;
}
/** Find all equipped weapon items with their SRD data. */
export function findEquippedWeapons(inventory, equipmentMap) {
    const result = [];
    for (const item of inventory) {
        if (!item.equipped)
            continue;
        if (item.item_type !== 'weapon')
            continue;
        const srd = equipmentMap.get(item.srd_slug);
        if (srd)
            result.push({ item, srd });
    }
    return result;
}
/** Check if a shield is equipped. */
export function hasEquippedShield(inventory, equipmentMap) {
    for (const item of inventory) {
        if (!item.equipped)
            continue;
        if (item.item_type !== 'shield')
            continue;
        const srd = equipmentMap.get(item.srd_slug);
        if (srd && srd.armorCategory === 'Shield')
            return true;
        // Option B: manual shield without an SRD slug — flat +2 downstream. The
        // shield bonus is applied once (`hasShield || manual.shieldOn`) so this
        // never double-counts with the server's separate `shield_on` knob.
        if (!srd)
            return true;
    }
    return false;
}
/**
 * Calculate AC per D&D 5e rules.
 * - No armor: 10 + DEX mod
 * - Light armor: acBase + DEX mod
 * - Medium armor: acBase + min(DEX mod, acMaxBonus ?? 2)
 * - Heavy armor: acBase (no DEX)
 * - Shield: +2
 * - Unarmored Defense (Barbarian): 10 + DEX mod + CON mod
 * - Unarmored Defense (Monk): 10 + DEX mod + WIS mod
 */
export function calculateAC(armor, hasShield, dexMod, classFeatures, conMod, wisMod) {
    let ac;
    if (armor) {
        const base = armor.acBase ?? 10;
        const category = armor.armorCategory ?? '';
        if (category === 'Heavy') {
            ac = base;
        }
        else if (category === 'Medium') {
            const cap = armor.acMaxBonus ?? 2;
            ac = base + Math.min(dexMod, cap);
        }
        else {
            // Light armor: full DEX
            ac = base + dexMod;
        }
    }
    else {
        // No armor — check Unarmored Defense
        if (classFeatures?.unarmoredDefense === 'barbarian') {
            ac = 10 + dexMod + (conMod ?? 0);
        }
        else if (classFeatures?.unarmoredDefense === 'monk') {
            ac = 10 + dexMod + (wisMod ?? 0);
        }
        else {
            ac = 10 + dexMod;
        }
    }
    if (hasShield) {
        ac += 2;
    }
    return ac;
}
/**
 * Itemise AC from raw inventory + ability mods + manual addends.
 * Pure; used by both the server (sheet-helpers.handleEquipChange) and the
 * client (Dnd5eSheetAdapter) so the breakdown shown matches the stored AC.
 */
export function computeAcBreakdown(inventoryRows, equipmentMap, dexMod, miscAc, classFeatures, conMod, wisMod, manual) {
    const inventory = parseInventory(inventoryRows);
    const armor = findEquippedArmor(inventory, equipmentMap);
    const hasShield = hasEquippedShield(inventory, equipmentMap);
    let base;
    let armorName;
    let appliedDex;
    let unarmoredBonus = 0;
    if (armor) {
        base = armor.acBase ?? 10;
        armorName = armor.name;
        const category = armor.armorCategory ?? '';
        if (category === 'Heavy') {
            appliedDex = 0;
        }
        else if (category === 'Medium') {
            appliedDex = Math.min(dexMod, armor.acMaxBonus ?? 2);
        }
        else {
            appliedDex = dexMod;
        }
    }
    else {
        base = 10;
        armorName = null;
        appliedDex = dexMod;
        if (classFeatures?.unarmoredDefense === 'barbarian')
            unarmoredBonus = conMod ?? 0;
        else if (classFeatures?.unarmoredDefense === 'monk')
            unarmoredBonus = wisMod ?? 0;
    }
    // The manual `armor_ac` addend stands in for armor NOT tracked as an inventory
    // item. When an armor item IS equipped it already supplies `base`, so counting
    // the manual bonus too would double the armor (the UI hides the manual control
    // in that case; here we make the arithmetic agree). Drop it when armor is worn.
    const armorBonus = armor ? 0 : (manual?.armorBonus ?? 0);
    const shieldBonus = (hasShield || manual?.shieldOn) ? 2 : 0;
    const total = base + armorBonus + appliedDex + shieldBonus + unarmoredBonus + miscAc;
    return { base, armorName, appliedDex, armorBonus, shieldBonus, unarmoredBonus, miscBonus: miscAc, total };
}
/**
 * Check if armor has a STR requirement penalty.
 * Returns true if the character doesn't meet the strMinimum.
 */
export function hasArmorStrPenalty(armor, str) {
    return (armor.strMinimum ?? 0) > str;
}
// --- Attack Row Building ---
/**
 * Build an attack row from a weapon's SRD data + character stats.
 * Finesse weapons use max(strMod, dexMod).
 */
export function buildAttackRow(weapon, strMod, dexMod, profBonus) {
    const isFinesse = weapon.properties?.includes('Finesse') ?? false;
    const isRanged = weapon.weaponRange === 'Ranged';
    // Determine ability modifier
    let abilityMod;
    if (isFinesse) {
        abilityMod = Math.max(strMod, dexMod);
    }
    else if (isRanged) {
        abilityMod = dexMod;
    }
    else {
        abilityMod = strMod;
    }
    const toHit = abilityMod + profBonus;
    const damageDice = weapon.damageDice ?? '1d4';
    const damageBonus = abilityMod;
    const damageType = weapon.damageType?.toLowerCase() ?? 'bludgeoning';
    // Build properties string
    const props = [];
    if (weapon.properties)
        props.push(...weapon.properties);
    if (weapon.rangeNormal && weapon.rangeLong) {
        props.push(`${weapon.rangeNormal}/${weapon.rangeLong} ft`);
    }
    else if (weapon.throwRange && weapon.throwRangeLong) {
        props.push(`${weapon.throwRange}/${weapon.throwRangeLong} ft`);
    }
    return {
        rowId: `weapon-${weapon.slug}`,
        values: {
            name: weapon.name,
            to_hit: toHit,
            damage_dice: damageBonus >= 0 ? `${damageDice}+${damageBonus}` : `${damageDice}${damageBonus}`,
            damage_type: damageType,
            properties: props.join(', '),
        },
    };
}
